import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import {
    FiArrowLeft,
    FiCreditCard,
    FiUser,
    FiBriefcase,
    FiMapPin,
    FiCalendar,
    FiDollarSign,
    FiTag,
    FiShield,
    FiHash,
    FiGlobe,
    FiMail,
    FiPhone,
    FiInfo,
    FiClock,
} from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTripPolicyByIdRequest } from '../../store/actions/tripsActions';
import PolicyInfoCard, { formatPolicyDate } from '../../components/Common/PolicyInfoCard';
import PolicyCommercialGestureButton from './commercialGestures/PolicyCommercialGestureBlock';
import './AdminTripPolicyDetail.css';

const MISSING_LABEL = 'Non renseigné';

const isEmptyValue = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed === '' || trimmed.toLowerCase() === 'unknown' || trimmed.toLowerCase() === 'n/a';
    }
    return false;
};

const MissingValue = () => (
    <span className="atpd-detail-value-missing">{MISSING_LABEL}</span>
);

const displayText = (value) => {
    if (isEmptyValue(value)) return <MissingValue />;
    return String(value).trim();
};

const displayBoolean = (value) => {
    if (value === null || value === undefined) return <MissingValue />;
    return (
        <Badge bg={value ? 'success' : 'secondary'}>
            {value ? 'Oui' : 'Non'}
        </Badge>
    );
};

const formatDisplayDate = (value) => {
    const formatted = formatPolicyDate(value);
    if (!formatted) return null;
    return formatted;
};

const fullName = (person) => {
    if (!person) return null;
    const name = [person.first_name, person.last_name]
        .filter((part) => !isEmptyValue(part))
        .map((part) => String(part).trim())
        .join(' ');
    return name || null;
};

const policyStatusBadgeVariant = (status) => {
    const normalized = String(status || '').trim();
    if (normalized === 'Created') return 'success';
    if (normalized === 'Paid') return 'primary';
    if (normalized === 'CG confirmed') return 'info';
    if (normalized === 'Cancelled') return 'danger';
    return 'secondary';
};

const clientStatusBadgeVariant = (status) => {
    const normalized = String(status || '').trim().toLowerCase();
    if (normalized === 'active' || normalized === 'actif') return 'success';
    return 'secondary';
};

const formatDateRange = (start, end) => {
    const startFormatted = formatDisplayDate(start);
    const endFormatted = formatDisplayDate(end);
    if (!startFormatted && !endFormatted) return null;
    return [startFormatted, endFormatted].filter(Boolean).join(' → ');
};

const PageHeader = ({ policyCode, status, onBack, commercialGestureButton = null }) => (
    <div className="page-header">
        <div>
            <h2 className="page-title">
                <span className="page-title-icon" aria-hidden="true">
                    <FiCreditCard />
                </span>
                Détail de la police
            </h2>
            <p className="text-muted mb-0">
                {policyCode ? (
                    <>
                        Code police :{' '}
                        <span className="atpd-detail-value--mono">{policyCode}</span>
                    </>
                ) : (
                    'Consultez les informations complètes de la police séjour.'
                )}
                {status && (
                    <>
                        {' '}
                        <Badge bg={policyStatusBadgeVariant(status)} className="ms-1">
                            {status}
                        </Badge>
                    </>
                )}
            </p>
        </div>
        <div className="page-header-actions">
            {commercialGestureButton}
            <Button variant="outline-secondary" onClick={onBack}>
                <FiArrowLeft className="me-2" aria-hidden="true" />
                Retour
            </Button>
        </div>
    </div>
);

const DetailField = ({ label, icon: Icon, children, fullWidth = false, mono = false, highlight = false }) => (
    <div className={['atpd-detail-field', fullWidth ? 'atpd-detail-field--full' : ''].filter(Boolean).join(' ')}>
        <div className="atpd-detail-label">
            {Icon ? <Icon size={14} aria-hidden="true" /> : null}
            {label}
        </div>
        <div
            className={[
                'atpd-detail-value',
                mono ? 'atpd-detail-value--mono' : '',
                highlight ? 'atpd-detail-value--updated' : '',
            ].filter(Boolean).join(' ')}
        >
            {children ?? <MissingValue />}
        </div>
    </div>
);

const DetailSection = ({ id, title, icon: Icon, children }) => (
    <Card id={id} className="admin-ds-section-card atpd-section-card mb-3">
        <Card.Header>
            {Icon ? <Icon className="atpd-section-card__icon" aria-hidden="true" /> : null}
            <span className="atpd-section-card__title">{title}</span>
        </Card.Header>
        <Card.Body>
            <div className="atpd-detail-grid">{children}</div>
        </Card.Body>
    </Card>
);

const KpiCard = ({ icon: Icon, label, value, sub }) => (
    <div className="atpd-kpi-card">
        <span className="atpd-kpi-card__icon" aria-hidden="true">
            <Icon size={18} />
        </span>
        <div className="atpd-kpi-card__body">
            <span className="atpd-kpi-card__label">{label}</span>
            <span className="atpd-kpi-card__value">{value ?? <MissingValue />}</span>
            {sub ? <span className="atpd-kpi-card__sub">{sub}</span> : null}
        </div>
    </div>
);

const SECTION_LINKS = [
    { id: 'atpd-policy', label: 'Police', icon: FiCreditCard },
    { id: 'atpd-financial', label: 'Finances', icon: FiDollarSign },
    { id: 'atpd-client', label: 'Client', icon: FiUser },
    { id: 'atpd-activity', label: 'Activité', icon: FiMapPin },
    { id: 'atpd-partner', label: 'Partenaire', icon: FiBriefcase },
    { id: 'atpd-reference', label: 'Référence', icon: FiTag },
    { id: 'atpd-subcoverage', label: 'Sous-couverture', icon: FiShield },
];

const AdminTripPolicyDetail = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { policyId } = useParams();
    const { selectedPolicy } = useSelector((state) => state.trips);

    useEffect(() => {
        if (policyId) {
            dispatch(fetchTripPolicyByIdRequest(policyId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [policyId]);

    const handleBack = () => navigate('/admin/trips/policies');

    const policyInfo = selectedPolicy?.data?.policies_info || selectedPolicy?.data || null;
    const clientInfo = policyInfo?.client_info || {};
    const activityInfo = policyInfo?.activity_info || {};
    const partnerInfo = policyInfo?.partner_info || {};
    const referenceInfo = policyInfo?.reference_info || {};
    const subcoverageTrip = policyInfo?.subcoverage_trip || {};
    const hasSubcoverage = subcoverageTrip && Object.keys(subcoverageTrip).length > 0;

    if (selectedPolicy.loading) {
        return (
            <div className="dashboard-page admin-trip-policy-detail-page">
                <Card>
                    <Card.Body>
                        <PageHeader onBack={handleBack} />
                        <div className="atpd-loading-shell">
                            <Spinner animation="border" variant="primary" />
                            <p>Chargement de la police…</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (selectedPolicy.error) {
        return (
            <div className="dashboard-page admin-trip-policy-detail-page">
                <Card>
                    <Card.Body>
                        <PageHeader onBack={handleBack} />
                        <Alert variant="danger">{selectedPolicy.error}</Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!policyInfo) {
        return (
            <div className="dashboard-page admin-trip-policy-detail-page">
                <Card>
                    <Card.Body>
                        <PageHeader onBack={handleBack} />
                        <p className="text-muted mb-0">
                            Aucune donnée de police disponible pour l&apos;identifiant :{' '}
                            <span className="atpd-detail-value--mono">{policyId}</span>.
                        </p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const clientName = fullName(clientInfo);
    const stayDates = formatDateRange(
        activityInfo.activity_start_date,
        activityInfo.activity_end_date
    );
    const tscaAmount = policyInfo['tsca_amount '] ?? policyInfo.tsca_amount;
    const visibleSectionLinks = hasSubcoverage
        ? SECTION_LINKS
        : SECTION_LINKS.filter((link) => link.id !== 'atpd-subcoverage');

    return (
        <div className="dashboard-page admin-trip-policy-detail-page">
            <Card>
                <Card.Body>
                    <PageHeader
                        policyCode={policyInfo.policy_code}
                        status={policyInfo.status}
                        onBack={handleBack}
                        commercialGestureButton={(
                            <PolicyCommercialGestureButton
                                policyId={policyId}
                                policyPreview={{
                                    policy_id: policyId,
                                    policy_code: policyInfo.policy_code,
                                    reservation_number: activityInfo.reservation_number,
                                    first_name: clientInfo.first_name,
                                    last_name: clientInfo.last_name,
                                    mail: clientInfo.email,
                                }}
                            />
                        )}
                    />

                    <div className="atpd-kpi-strip" role="list" aria-label="Résumé de la police">
                        <KpiCard
                            icon={FiShield}
                            label="Statut"
                            value={
                                !isEmptyValue(policyInfo.status) ? (
                                    <Badge bg={policyStatusBadgeVariant(policyInfo.status)}>
                                        {policyInfo.status}
                                    </Badge>
                                ) : (
                                    <MissingValue />
                                )
                            }
                            sub={!isEmptyValue(policyInfo.handling_mode) ? policyInfo.handling_mode : null}
                        />
                        <KpiCard
                            icon={FiCalendar}
                            label="Séjour"
                            value={stayDates || <MissingValue />}
                            sub={
                                !isEmptyValue(policyInfo.trip_number_nights)
                                    ? `${policyInfo.trip_number_nights} nuit(s)`
                                    : null
                            }
                        />
                        <KpiCard
                            icon={FiBriefcase}
                            label="Partenaire"
                            value={displayText(partnerInfo.company_name)}
                            sub={clientName || null}
                        />
                        <KpiCard
                            icon={FiDollarSign}
                            label="Prix TTC"
                            value={displayText(policyInfo.price_incl_tsca)}
                            sub={
                                !isEmptyValue(policyInfo.price_incl_tsca_after_discount)
                                    ? `Après remise : ${policyInfo.price_incl_tsca_after_discount}`
                                    : null
                            }
                        />
                    </div>

                    {activityInfo.trip_modified && (
                        <Alert variant="warning" className="atpd-alert-modified">
                            <FiInfo className="me-2" aria-hidden="true" />
                            Les dates du séjour ont été modifiées. Consultez les sections Activité et Information
                            police pour les nouvelles valeurs.
                        </Alert>
                    )}

                    <nav className="atpd-section-nav" aria-label="Navigation des sections">
                        {visibleSectionLinks.map(({ id, label, icon: Icon }) => (
                            <a key={id} href={`#${id}`} className="atpd-section-nav__link">
                                <Icon size={14} aria-hidden="true" />
                                {label}
                            </a>
                        ))}
                    </nav>

                    <PolicyInfoCard
                        companyName={partnerInfo.company_name}
                        policyCode={policyInfo.policy_code}
                        reservationNumber={activityInfo.reservation_number}
                        stayStartDate={activityInfo.activity_start_date}
                        stayEndDate={activityInfo.activity_end_date}
                        newStayStartDate={activityInfo.new_trip_start_date}
                        newStayEndDate={activityInfo.new_trip_end_date}
                        effectiveStartDate={activityInfo.trip_effective_start_date}
                        effectiveEndDate={activityInfo.trip_effective_end_date}
                        newEffectiveStartDate={activityInfo.new_trip_effective_start_date}
                        newEffectiveEndDate={activityInfo.new_trip_effective_end_date}
                        referenceName={referenceInfo.reference_name}
                        address={activityInfo.activity_address}
                        latitude={activityInfo.latitude}
                        longitude={activityInfo.longitude}
                        dailySumInsuredMaintain={policyInfo.daily_sum_insured_maintain}
                        dailySumInsuredCancellation={policyInfo.daily_sum_insured_cancellation}
                        totalSumInsuredMaintain={policyInfo.total_sum_insured_maintain}
                        totalSumInsuredCancellation={policyInfo.total_sum_insured_cancellation}
                    />

                    <DetailSection id="atpd-policy" title="Informations principales" icon={FiCreditCard}>
                        <DetailField label="Code police" icon={FiHash} mono>
                            {displayText(policyInfo.policy_code)}
                        </DetailField>
                        <DetailField label="Statut" icon={FiShield}>
                            {!isEmptyValue(policyInfo.status) ? (
                                <Badge bg={policyStatusBadgeVariant(policyInfo.status)}>
                                    {policyInfo.status}
                                </Badge>
                            ) : (
                                <MissingValue />
                            )}
                        </DetailField>
                        <DetailField label="Date UW" icon={FiCalendar}>
                            {formatDisplayDate(policyInfo.uw_date) || <MissingValue />}
                        </DetailField>
                        <DetailField label="Code devis" icon={FiHash} mono>
                            {displayText(policyInfo.quote_code)}
                        </DetailField>
                        <DetailField label="Mode de traitement" icon={FiBriefcase}>
                            {displayText(policyInfo.handling_mode)}
                        </DetailField>
                        <DetailField label="Langue préférée" icon={FiGlobe}>
                            {displayText(policyInfo.preferred_language)}
                        </DetailField>
                        <DetailField label="Nombre de nuits" icon={FiCalendar}>
                            {displayText(policyInfo.trip_number_nights)}
                        </DetailField>
                        <DetailField label="Nuits déclenchées" icon={FiCalendar}>
                            {policyInfo.nb_nights_triggered ?? 0}
                        </DetailField>
                        <DetailField
                            label="Nouveau nombre de nuits"
                            icon={FiCalendar}
                            highlight={!isEmptyValue(policyInfo.new_trip_number_nights)}
                        >
                            {displayText(policyInfo.new_trip_number_nights)}
                        </DetailField>
                        <DetailField
                            label="Nouvelles nuits déclenchées"
                            icon={FiCalendar}
                            highlight={!isEmptyValue(policyInfo.new_nb_nights_triggered)}
                        >
                            {displayText(policyInfo.new_nb_nights_triggered)}
                        </DetailField>
                        <DetailField label="Jours couverts" icon={FiCalendar}>
                            {displayText(policyInfo.covered_days)}
                        </DetailField>
                        <DetailField
                            label="Nouveaux jours couverts"
                            icon={FiCalendar}
                            highlight={!isEmptyValue(policyInfo.new_covered_days)}
                        >
                            {displayText(policyInfo.new_covered_days)}
                        </DetailField>
                        <DetailField label="Tracking code" icon={FiHash} mono>
                            {displayText(policyInfo.tracking_code)}
                        </DetailField>
                        <DetailField label="Code technique" icon={FiHash} mono>
                            {displayText(policyInfo.technical_premium)}
                        </DetailField>
                    </DetailSection>

                    <DetailSection id="atpd-financial" title="Informations financières" icon={FiDollarSign}>
                        <DetailField label="Prix assurance HT" icon={FiDollarSign}>
                            {displayText(policyInfo.insurance_price_excl_tsca)}
                        </DetailField>
                        <DetailField label="Ratio prix Orakle HT" icon={FiDollarSign}>
                            {displayText(policyInfo.orakle_price_ratio_excl_tsca)}
                        </DetailField>
                        <DetailField label="Prix TTC" icon={FiDollarSign}>
                            {displayText(policyInfo.price_incl_tsca)}
                        </DetailField>
                        <DetailField label="Montant TSCA" icon={FiDollarSign}>
                            {displayText(tscaAmount)}
                        </DetailField>
                        <DetailField label="Taux TSCA" icon={FiDollarSign}>
                            {displayText(policyInfo.tsca)}
                        </DetailField>
                        <DetailField label="Prix TTC après remise" icon={FiDollarSign}>
                            {displayText(policyInfo.price_incl_tsca_after_discount)}
                        </DetailField>
                        <DetailField label="Prime technique" icon={FiDollarSign}>
                            {displayText(policyInfo.technical_premium_amount)}
                        </DetailField>
                        <DetailField label="Montant TVA" icon={FiDollarSign}>
                            {displayText(policyInfo.tva_amount)}
                        </DetailField>
                        <DetailField label="Pourcentage TVA" icon={FiDollarSign}>
                            {displayText(policyInfo.tva_percent)}
                        </DetailField>
                        <DetailField label="Montant journalier – Maintien" icon={FiDollarSign}>
                            {displayText(policyInfo.daily_sum_insured_maintain)}
                        </DetailField>
                        <DetailField label="Montant journalier – Annulation" icon={FiDollarSign}>
                            {displayText(policyInfo.daily_sum_insured_cancellation)}
                        </DetailField>
                        <DetailField label="Montant total – Maintien" icon={FiDollarSign}>
                            {displayText(policyInfo.total_sum_insured_maintain)}
                        </DetailField>
                        <DetailField label="Montant total – Annulation" icon={FiDollarSign}>
                            {displayText(policyInfo.total_sum_insured_cancellation)}
                        </DetailField>
                    </DetailSection>

                    <DetailSection id="atpd-client" title="Informations client" icon={FiUser}>
                        <DetailField label="Nom" icon={FiUser}>
                            {clientName || <MissingValue />}
                        </DetailField>
                        <DetailField label="Email" icon={FiMail}>
                            {displayText(clientInfo.email)}
                        </DetailField>
                        <DetailField label="Code client" icon={FiHash} mono>
                            {displayText(clientInfo.client_code)}
                        </DetailField>
                        <DetailField label="Statut" icon={FiShield}>
                            {!isEmptyValue(clientInfo.status) ? (
                                <Badge bg={clientStatusBadgeVariant(clientInfo.status)}>
                                    {clientInfo.status}
                                </Badge>
                            ) : (
                                <MissingValue />
                            )}
                        </DetailField>
                        <DetailField label="Date d'inscription" icon={FiCalendar}>
                            {formatDisplayDate(clientInfo.date_joined) || <MissingValue />}
                        </DetailField>
                        <DetailField label="Date de naissance" icon={FiCalendar}>
                            {formatDisplayDate(clientInfo.birth_date) || <MissingValue />}
                        </DetailField>
                        <DetailField label="Ville" icon={FiMapPin}>
                            {displayText(clientInfo.city)}
                        </DetailField>
                        <DetailField label="Pays" icon={FiGlobe}>
                            {displayText(clientInfo.country)}
                        </DetailField>
                        <DetailField label="Code postal" icon={FiMapPin}>
                            {displayText(clientInfo.zip_code)}
                        </DetailField>
                        <DetailField label="Téléphone" icon={FiPhone}>
                            {displayText(clientInfo.phone_number)}
                        </DetailField>
                        <DetailField label="Lieu de naissance" icon={FiMapPin}>
                            {displayText(clientInfo.place_of_birth)}
                        </DetailField>
                        <DetailField label="Pays de naissance" icon={FiGlobe}>
                            {displayText(clientInfo.country_of_birth)}
                        </DetailField>
                        <DetailField label="IBAN reçu" icon={FiCreditCard}>
                            {displayBoolean(clientInfo.is_iban_received)}
                        </DetailField>
                    </DetailSection>

                    <DetailSection id="atpd-activity" title="Informations activité" icon={FiMapPin}>
                        <DetailField label="Nom" icon={FiMapPin}>
                            {displayText(activityInfo.name)}
                        </DetailField>
                        <DetailField label="Catégorie" icon={FiTag}>
                            {displayText(activityInfo.category)}
                        </DetailField>
                        <DetailField label="Code activité" icon={FiHash} mono>
                            {displayText(activityInfo.activity_code)}
                        </DetailField>
                        <DetailField label="Date de début" icon={FiCalendar}>
                            {formatDisplayDate(activityInfo.activity_start_date) || <MissingValue />}
                        </DetailField>
                        <DetailField label="Date de fin" icon={FiCalendar}>
                            {formatDisplayDate(activityInfo.activity_end_date) || <MissingValue />}
                        </DetailField>
                        <DetailField label="Heure de début" icon={FiClock}>
                            {displayText(activityInfo.activity_start_time)}
                        </DetailField>
                        <DetailField label="Heure de fin" icon={FiClock}>
                            {displayText(activityInfo.activity_end_time)}
                        </DetailField>
                        <DetailField label="Date effective début" icon={FiCalendar}>
                            {formatDisplayDate(activityInfo.trip_effective_start_date) || <MissingValue />}
                        </DetailField>
                        <DetailField label="Date effective fin" icon={FiCalendar}>
                            {formatDisplayDate(activityInfo.trip_effective_end_date) || <MissingValue />}
                        </DetailField>
                        <DetailField
                            label="Nouvelle date début"
                            icon={FiCalendar}
                            highlight={!isEmptyValue(activityInfo.new_trip_start_date)}
                        >
                            {formatDisplayDate(activityInfo.new_trip_start_date) || <MissingValue />}
                        </DetailField>
                        <DetailField
                            label="Nouvelle date fin"
                            icon={FiCalendar}
                            highlight={!isEmptyValue(activityInfo.new_trip_end_date)}
                        >
                            {formatDisplayDate(activityInfo.new_trip_end_date) || <MissingValue />}
                        </DetailField>
                        <DetailField
                            label="Nouvelle date effective début"
                            icon={FiCalendar}
                            highlight={!isEmptyValue(activityInfo.new_trip_effective_start_date)}
                        >
                            {formatDisplayDate(activityInfo.new_trip_effective_start_date) || <MissingValue />}
                        </DetailField>
                        <DetailField
                            label="Nouvelle date effective fin"
                            icon={FiCalendar}
                            highlight={!isEmptyValue(activityInfo.new_trip_effective_end_date)}
                        >
                            {formatDisplayDate(activityInfo.new_trip_effective_end_date) || <MissingValue />}
                        </DetailField>
                        <DetailField label="Prix activité" icon={FiDollarSign}>
                            {displayText(activityInfo.activity_price)}
                        </DetailField>
                        <DetailField
                            label="Nouveau prix"
                            icon={FiDollarSign}
                            highlight={!isEmptyValue(activityInfo.new_trip_price)}
                        >
                            {displayText(activityInfo.new_trip_price)}
                        </DetailField>
                        <DetailField label="Date modifiée" icon={FiInfo}>
                            {displayBoolean(activityInfo.trip_modified)}
                        </DetailField>
                        <DetailField label="Numéro de réservation" icon={FiHash} mono>
                            {displayText(activityInfo.reservation_number)}
                        </DetailField>
                        <DetailField label="Adresse" icon={FiMapPin} fullWidth>
                            {displayText(activityInfo.activity_address)}
                        </DetailField>
                        <DetailField label="Ville" icon={FiMapPin}>
                            {displayText(activityInfo.activity_city)}
                        </DetailField>
                        <DetailField label="Code postal" icon={FiMapPin}>
                            {displayText(activityInfo.activity_zip_code)}
                        </DetailField>
                        <DetailField label="Pays" icon={FiGlobe}>
                            {displayText(activityInfo.activity_country)}
                        </DetailField>
                        <DetailField label="Latitude" icon={FiGlobe} mono>
                            {displayText(activityInfo.latitude)}
                        </DetailField>
                        <DetailField label="Longitude" icon={FiGlobe} mono>
                            {displayText(activityInfo.longitude)}
                        </DetailField>
                    </DetailSection>

                    <DetailSection id="atpd-partner" title="Informations partenaire" icon={FiBriefcase}>
                        <DetailField label="Nom" icon={FiUser}>
                            {fullName({
                                first_name: partnerInfo.partner_first_name,
                                last_name: partnerInfo.partner_last_name,
                            }) || <MissingValue />}
                        </DetailField>
                        <DetailField label="Entreprise" icon={FiBriefcase}>
                            {displayText(partnerInfo.company_name)}
                        </DetailField>
                        <DetailField label="Email" icon={FiMail}>
                            {displayText(partnerInfo.email)}
                        </DetailField>
                        <DetailField label="Téléphone" icon={FiPhone}>
                            {displayText(partnerInfo.phone_number)}
                        </DetailField>
                        <DetailField label="Ville" icon={FiMapPin}>
                            {displayText(partnerInfo.city)}
                        </DetailField>
                        <DetailField label="Pays" icon={FiGlobe}>
                            {displayText(partnerInfo.country)}
                        </DetailField>
                        <DetailField label="Code" icon={FiHash} mono>
                            {displayText(partnerInfo.code)}
                        </DetailField>
                        <DetailField label="Commission" icon={FiDollarSign}>
                            {displayText(partnerInfo.commission)}
                        </DetailField>
                        <DetailField label="Compte activé" icon={FiShield}>
                            {displayBoolean(partnerInfo.activate_account)}
                        </DetailField>
                        <DetailField label="Vérifié" icon={FiShield}>
                            {displayBoolean(partnerInfo.is_verified)}
                        </DetailField>
                    </DetailSection>

                    <DetailSection id="atpd-reference" title="Informations référence" icon={FiTag}>
                        <DetailField label="Nom" icon={FiTag}>
                            {displayText(referenceInfo.reference_name)}
                        </DetailField>
                        <DetailField label="Code référence" icon={FiHash} mono>
                            {displayText(referenceInfo.reference_code)}
                        </DetailField>
                        <DetailField label="Catégorie" icon={FiTag}>
                            {displayText(referenceInfo.reference_category)}
                        </DetailField>
                        <DetailField label="Déclenchement cumulé" icon={FiInfo}>
                            {displayBoolean(referenceInfo.prcp_is_cumulated)}
                        </DetailField>
                    </DetailSection>

                    {hasSubcoverage && (
                        <DetailSection id="atpd-subcoverage" title="Informations sous-couverture" icon={FiShield}>
                            <DetailField label="Code sous-couverture" icon={FiHash} mono>
                                {displayText(subcoverageTrip.subcoverage_trip_code)}
                            </DetailField>
                            <DetailField label="Type de déclencheur" icon={FiShield}>
                                {displayText(subcoverageTrip.trigger_type)}
                            </DetailField>
                            <DetailField label="Unité" icon={FiTag}>
                                {displayText(subcoverageTrip.unit)}
                            </DetailField>
                            <DetailField label="Nombre d'heures de déclenchement" icon={FiClock}>
                                {displayText(subcoverageTrip.trigger_number_hours)}
                            </DetailField>
                            <DetailField label="Heure de déclenchement inférieure" icon={FiClock}>
                                {displayText(subcoverageTrip.prcp_trigger_hour_inf)}
                            </DetailField>
                            <DetailField label="Date début sous-couverture" icon={FiCalendar}>
                                {formatDisplayDate(subcoverageTrip.sub_coverage_start_date) || <MissingValue />}
                            </DetailField>
                            <DetailField label="Date fin sous-couverture" icon={FiCalendar}>
                                {formatDisplayDate(subcoverageTrip.sub_coverage_end_date) || <MissingValue />}
                            </DetailField>
                            <DetailField label="Heure début journée" icon={FiClock}>
                                {displayText(subcoverageTrip.day_start_time)}
                            </DetailField>
                            <DetailField label="Heure fin journée" icon={FiClock}>
                                {displayText(subcoverageTrip.day_end_time)}
                            </DetailField>
                            <DetailField label="Ratio prix Orakle HT" icon={FiDollarSign}>
                                {displayText(subcoverageTrip.orakle_price_ratio_excl_tsca)}
                            </DetailField>
                            <DetailField label="Type de distribution" icon={FiTag}>
                                {displayText(subcoverageTrip.distribution_type)}
                            </DetailField>
                            <DetailField label="Plan de paiement" icon={FiCreditCard}>
                                {displayText(subcoverageTrip.payment_schedule)}
                            </DetailField>
                            <DetailField label="Source de données maintien" icon={FiInfo}>
                                {displayText(subcoverageTrip.maintain_data_source)}
                            </DetailField>
                            <DetailField label="Source de données annulation" icon={FiInfo}>
                                {displayText(subcoverageTrip.cancellation_data_source)}
                            </DetailField>
                            <DetailField label="Ville" icon={FiMapPin}>
                                {displayText(subcoverageTrip.city)}
                            </DetailField>
                            <DetailField label="Code postal" icon={FiMapPin}>
                                {displayText(subcoverageTrip.zip_code)}
                            </DetailField>
                            <DetailField label="Pays" icon={FiGlobe}>
                                {displayText(subcoverageTrip.country)}
                            </DetailField>
                            <DetailField label="Latitude" icon={FiGlobe} mono>
                                {displayText(subcoverageTrip.geo_latitude)}
                            </DetailField>
                            <DetailField label="Longitude" icon={FiGlobe} mono>
                                {displayText(subcoverageTrip.geo_longitude)}
                            </DetailField>
                        </DetailSection>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripPolicyDetail;
